/**
 * Validation for report templates, preparation, issuance, lifecycle and
 * delivery — module C9, Phase 10.
 *
 * READ CLAUDE.md SECTIONS 4, 6.1 AND 8 FIRST.
 *
 * A REPORT PRESENTS APPROVED RESULTS; IT DOES NOT PRODUCE ONE. There is no
 * schema in this file for a reading, a unit, a formula, a rounding rule, an
 * acceptance limit, a chart or a conformity statement — for any test,
 * including the five initial ones — because that is test science and it is
 * gated on a laboratory-validated worked example. What a preparer writes is
 * WHICH approved results go in the document, in WHICH order, and their own
 * remarks.
 *
 * NO LABORATORY'S OWN VALUES ARE VALIDATED INTO EXISTENCE HERE either
 * (section 6.1): no template is named, no footer wording is defaulted, no
 * number is formatted. The footer text below has no product default, because
 * no wording this product could invent belongs to any laboratory.
 *
 * EVERY BODY SCHEMA IS STRICT. An unknown key is refused with a 400 rather
 * than silently dropped, so a client that sends `reportNumber`, `issuedBy`,
 * `status` or `conformity` learns immediately that no such thing can be
 * written here: a number is allocated at issuance by the backend (D113), and a
 * status is reached by an act, never by a field.
 *
 * WHY `draftRevision` IS REQUIRED ON EVERY EDIT AND ON ISSUANCE: the issuer
 * issues what they previewed (D125). If the draft has been changed since it
 * was read, the echoed number no longer matches and the server refuses with a
 * conflict rather than issuing a document nobody looked at.
 */
import { z } from 'zod';
import { ALL_REPORT_CORRECTION_IMPACTS, ALL_REPORT_DELIVERY_METHODS, ALL_REPORT_DELIVERY_PURPOSES, ALL_REPORT_DELIVERY_STATUSES, ALL_REPORT_LANGUAGES, ALL_REPORT_STATUSES, ALL_REPORT_TEMPLATE_STATUSES, ALL_REPORT_NOTES_SOURCES, ALL_REPORT_RESULT_ELEMENTS, ALL_REPORT_SECTIONS, ALL_RESULT_TEMPLATES_FOR_SAMPLE, REPORT_DELIVERY_PURPOSE, correctionRequiresAffectedResults, } from '@lims/shared-types';
import { boundedText, isoDateSchema, isoTimestampSchema, listQuerySchema, reasonSchema, uuidSchema, } from './primitives.js';
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    // An empty string is an absent value, not a value of "". Storing "" would
    // make an empty field look like a recorded fact.
    .transform((value) => (value === '' ? null : value));
export const reportLanguageSchema = z.enum(ALL_REPORT_LANGUAGES);
export const reportTemplateStatusSchema = z.enum(ALL_REPORT_TEMPLATE_STATUSES);
export const reportStatusSchema = z.enum(ALL_REPORT_STATUSES);
export const reportDeliveryStatusSchema = z.enum(ALL_REPORT_DELIVERY_STATUSES);
export const reportDeliveryPurposeSchema = z.enum(ALL_REPORT_DELIVERY_PURPOSES);
export const reportDeliveryMethodSchema = z.enum(ALL_REPORT_DELIVERY_METHODS);
export const reportCorrectionImpactSchema = z.enum(ALL_REPORT_CORRECTION_IMPACTS);
/**
 * The draft revision the person acted on, as they saw it. Positive, because a
 * draft is counted from its first save.
 */
const draftRevisionSchema = z.coerce.number().int().positive();
/**
 * A template's name, in one or both languages.
 *
 * Not `bilingualTextSchema`: that primitive allows 500 characters, and a
 * template name is a label in a picker rather than a paragraph. The shape and
 * the at-least-one-language rule are the same, because a record with no
 * readable name in either language is a record nobody can find.
 */
const templateNameSchema = z
    .object({
    ar: z.string().trim().max(200).nullable().optional(),
    en: z.string().trim().max(200).nullable().optional(),
})
    .strict()
    .refine((value) => Boolean(value.ar?.length) || Boolean(value.en?.length), {
    message: 'A name in at least one language is required.',
});
/**
 * The languages a template can render.
 *
 * A non-empty set, each value once. A template that renders nothing is not a
 * template, and a duplicate says nothing a single entry does not.
 */
const templateLanguagesSchema = z
    .array(reportLanguageSchema)
    .min(1)
    .max(ALL_REPORT_LANGUAGES.length)
    .refine((value) => new Set(value).size === value.length, {
    message: 'Each language may be listed once.',
});
/**
 * The approved results to print, IN PRINT ORDER.
 *
 * The order is the preparer's, and it is kept: a laboratory arranges a
 * document the way its client reads it. Each result appears once — printing
 * the same result twice in one document would make a later correction
 * ambiguous about which entry it concerned.
 *
 * The bound of 200 stops a typo asking for an impossible document; it is not a
 * statement about how many tests belong in a report. Whether each id is an
 * APPROVED result of the right work order is the backend's check, made when
 * the draft is built and again inside the issuance transaction (D124) — this
 * schema knows only that they are identifiers.
 */
const attemptIdsSchema = z
    .array(uuidSchema)
    .min(1)
    .max(200)
    .refine((value) => new Set(value).size === value.length, {
    message: 'A result may appear only once in a report.',
});
// ---------------------------------------------------------------------------
// Report settings (extends the laboratory's settings)
// ---------------------------------------------------------------------------
/**
 * The laboratory's own footer wording, printed at the foot of its reports.
 *
 * THIS PRODUCT SUPPLIES NO DEFAULT TEXT, in either language (D126,
 * `CLAUDE.md` section 6.1). A footer says something on a laboratory's own
 * behalf — a confidentiality note, a scope statement, a disclaimer — and no
 * wording invented here would belong to the laboratory that printed it.
 * `null` means null: the document simply carries no footer text.
 *
 * Unlike `bilingualTextSchema` there is deliberately NO at-least-one-language
 * rule, because "no footer at all" is a legitimate answer and a laboratory
 * that renders reports in one language only has nothing to say in the other.
 */
export const reportFooterTextSchema = z
    .object({
    ar: z
        .string()
        .trim()
        .max(1000)
        .nullable()
        .optional()
        .transform((value) => (value === '' ? null : value)),
    en: z
        .string()
        .trim()
        .max(1000)
        .nullable()
        .optional()
        .transform((value) => (value === '' ? null : value)),
})
    .strict();
// ---------------------------------------------------------------------------
// Templates (10a)
// ---------------------------------------------------------------------------
/** An ordered list naming every known key exactly once. */
const orderedKeysSchema = (keys) => z
    .array(z
    .object({
    key: z.enum(keys),
    visible: z.boolean(),
})
    .strict())
    .length(keys.length)
    .refine((entries) => new Set(entries.map((entry) => entry.key)).size === keys.length, {
    message: 'Every section is listed exactly once.',
});
const booleanFlags = (names) => z
    .object(Object.fromEntries(names.map((name) => [name, z.boolean()])))
    .strict();
/**
 * A template's section layout (D251). STRUCTURED, never free-form: the known
 * sections and result elements, each visible or hidden, in the laboratory's
 * order, and a few presentation options. Nothing here names a field, a unit or
 * a calculation — the specialised-results area is the test's own (`CLAUDE.md`
 * section 4).
 */
export const reportLayoutConfigSchema = z
    .object({
    version: z.literal(1),
    sections: orderedKeysSchema(ALL_REPORT_SECTIONS),
    resultElements: orderedKeysSchema(ALL_REPORT_RESULT_ELEMENTS),
    header: booleanFlags(['showLogo', 'showAddress', 'showContacts', 'showRegistrations']),
    reportInformation: booleanFlags([
        'showReportNumber',
        'showDateReported',
        'showWorkOrder',
        'showClient',
        'showProject',
        'showProjectLocation',
    ]),
    signatures: booleanFlags(['testedBy', 'reviewedBy', 'approvedBy']),
    notesSource: z.enum(ALL_REPORT_NOTES_SOURCES),
    footer: booleanFlags(['showFooterText', 'showReportIdentity', 'pageNumbering']),
    showQr: z.boolean(),
})
    .strict();
/**
 * The template's own notes / disclaimer, in each report language (D252). The
 * laboratory's wording; the product supplies none. Either side may be empty.
 */
export const templateNotesSchema = z
    .object({
    ar: z.string().trim().max(2000).nullable().optional()
        .transform((value) => (value === '' ? null : value)),
    en: z.string().trim().max(2000).nullable().optional()
        .transform((value) => (value === '' ? null : value)),
})
    .strict();
/**
 * Create a report template.
 *
 * There is no `layout` field: one layout exists (D112) and the backend sets
 * it. Header facts, logo and footer wording stay the laboratory's Organisation
 * and Report settings, read at render time (`CLAUDE.md` section 6.1); the
 * template only chooses what is SHOWN and in which order (`layoutConfig`,
 * D251), and may carry its own notes text (D252).
 */
export const createReportTemplateSchema = z
    .object({
    name: templateNameSchema,
    languages: templateLanguagesSchema,
    layoutConfig: reportLayoutConfigSchema.optional(),
    notes: templateNotesSchema.optional(),
    /** Make it the default for new reports. At most one active default exists
     *  per laboratory; the backend clears the previous one in the same
     *  transaction. */
    isDefault: z.boolean().optional(),
})
    .strict();
/**
 * Correct an ACTIVE template. Retiring has its own act, and a retired template
 * is never edited back into use.
 *
 * Every edit increments the template's revision, and an issued report carries
 * the revision it was built from, so editing a template changes no document
 * that already exists.
 */
export const updateReportTemplateSchema = z
    .object({
    name: templateNameSchema.optional(),
    languages: templateLanguagesSchema.optional(),
    layoutConfig: reportLayoutConfigSchema.optional(),
    notes: templateNotesSchema.optional(),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Provide at least one field to update.',
});
/**
 * Withdraw a template from use for new reports.
 *
 * Terminal, and it asks why: a laboratory has to be able to say later why a
 * form it used stopped being used. Reports already issued from it are
 * untouched.
 */
export const retireReportTemplateSchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
/**
 * Render a sample of a template.
 *
 * The preview is never stored, carries the laboratory's real header and a body
 * of visibly placeholder text, and is marked SAMPLE on every page (D127). It
 * contains no fabricated result, because a document that looked issued could
 * be printed and handed over as one.
 */
export const templatePreviewSchema = z
    .object({
    language: reportLanguageSchema,
    /**
     * `sample` (the default): the laboratory's real header with placeholders
     * — never a fabricated value. `result`: one APPROVED, locked result of
     * this laboratory, printed through this template, marked PREVIEW (D255).
     */
    source: z.enum(['sample', 'result']).optional(),
    /** With `source: result`: the approved attempt to print. */
    attemptId: uuidSchema.optional(),
    /** With `source: sample`: which test's results area to show in outline. */
    sampleResultTemplate: z
        .enum(ALL_RESULT_TEMPLATES_FOR_SAMPLE)
        .optional(),
})
    .strict()
    .refine((value) => value.source !== 'result' || value.attemptId !== undefined, {
    message: 'A preview from a result names the result.',
    path: ['attemptId'],
});
/**
 * Which template a report on these results would use when none is named
 * (D254): the one the tests name, otherwise the laboratory's default.
 */
export const templateResolutionQuerySchema = z
    .object({
    attemptIds: z
        .string()
        .transform((value) => value.split(',').map((part) => part.trim()).filter(Boolean))
        .pipe(attemptIdsSchema),
})
    .strict();
export const reportTemplateListQuerySchema = listQuerySchema.extend({
    status: reportTemplateStatusSchema.optional(),
});
// ---------------------------------------------------------------------------
// Preparing and issuing (10b)
// ---------------------------------------------------------------------------
/**
 * Prepare a report: create it with its first draft version.
 *
 * NO NUMBER IS REQUESTED OR ACCEPTED. A report number identifies a controlled
 * document, the document exists from issuance, and the backend allocates the
 * number then from the laboratory's own numbering rule (D113). A number
 * allocated for a draft would be consumed by every draft abandoned, leaving
 * gaps in a register with no document behind them.
 */
export const prepareReportSchema = z
    .object({
    workOrderId: uuidSchema,
    /** Omitted: the tests' own template, otherwise the laboratory default (D254). */
    templateId: uuidSchema.optional(),
    language: reportLanguageSchema,
    attemptIds: attemptIdsSchema,
    /** The laboratory's own remarks on this document. Not a finding, not an
     *  interpretation of one, and never required. */
    remarks: optionalText(4000),
})
    .strict();
/**
 * Edit the open draft.
 *
 * `draftRevision` is the revision the editor had in front of them; a mismatch
 * is refused rather than merged, because two people silently overwriting each
 * other is how a document goes out containing something nobody chose.
 */
export const updateReportDraftSchema = z
    .object({
    draftRevision: draftRevisionSchema,
    templateId: uuidSchema.optional(),
    language: reportLanguageSchema.optional(),
    attemptIds: attemptIdsSchema.optional(),
    /** `null` clears the remarks. An absent key leaves them as they are. */
    remarks: optionalText(4000),
})
    .strict()
    .refine((value) => Object.keys(value).length > 1, {
    message: 'Provide at least one field to change.',
});
/**
 * Issue the draft.
 *
 * The body is one field on purpose. Everything a document asserts —
 * its number, its date, its page count, its hash, who issued it — is recorded
 * by the backend from the act itself. A client that could send them could
 * assert a document was issued at a time it was not.
 */
export const issueReportSchema = z
    .object({
    draftRevision: draftRevisionSchema,
})
    .strict();
/**
 * Prepare a report AND issue it, in one act (D330).
 *
 * Exactly the preparation body: the draft is created and issued through the
 * same preparation and issuance rules inside ONE transaction, so a refusal of
 * either leaves nothing behind — no report, no draft, no number. There is
 * no draft revision to echo, because nobody else can have seen the draft.
 */
export const prepareAndIssueReportSchema = prepareReportSchema;
/**
 * Which language of an issued version to download (D330).
 *
 * Chosen EXPLICITLY by the person downloading, never from the interface
 * language. Omitted: the language the version was issued in. Both renderings
 * come from the same frozen snapshot; neither is a new version.
 */
export const reportFileQuerySchema = z
    .object({
    language: reportLanguageSchema.optional(),
})
    .strict();
/**
 * Abandon a draft of a report that was never issued.
 *
 * NOT a deletion (D131): the row, its preparer and this reason stay, because
 * somebody prepared it and the laboratory may need to say why it went no
 * further.
 */
export const discardReportDraftSchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
export const eligibleResultsQuerySchema = z.object({
    workOrderId: uuidSchema,
});
/**
 * The report register.
 *
 * `search` is inherited from `listQuerySchema` and matches the REPORT NUMBER —
 * the one identifier a client quotes on the telephone. Status and delivery
 * status are separate filters because they are separate facts: "issued" and
 * "delivered" are never the same question (`CLAUDE.md` section 8).
 */
export const reportRegisterQuerySchema = listQuerySchema.extend({
    status: reportStatusSchema.optional(),
    deliveryStatus: reportDeliveryStatusSchema.optional(),
    clientId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    workOrderId: uuidSchema.optional(),
    issuedFrom: isoDateSchema.optional(),
    issuedTo: isoDateSchema.optional(),
});
// ---------------------------------------------------------------------------
// Lifecycle (10c)
// ---------------------------------------------------------------------------
/**
 * Open a correction on an issued report.
 *
 * It creates the replacement DRAFT; it changes no result. A `scientific`
 * correction NAMES the results it concerns, and each must be approved again —
 * through the Phase 9 procedure, by the people who hold those permissions —
 * after this correction was opened, before the replacement may be issued
 * (D118). An `administrative` one names none, because it re-renders from
 * corrected linked records and reopens nothing.
 *
 * The rule is `iff`, both ways: a scientific correction without affected
 * results would wait for nothing, and an administrative correction that named
 * results would claim a reapproval it does not require.
 */
export const openReportCorrectionSchema = z
    .object({
    reason: reasonSchema,
    /** What the laboratory intends to put right, in its own words. */
    requestedChange: boundedText(3, 4000),
    impact: reportCorrectionImpactSchema,
    /** The results in the current version that the correction concerns. */
    affectedAttemptIds: z
        .array(uuidSchema)
        .min(1)
        .max(200)
        .refine((value) => new Set(value).size === value.length, {
        message: 'A result may be named only once.',
    })
        .optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    const needsAffected = correctionRequiresAffectedResults(value.impact);
    if (needsAffected && !value.affectedAttemptIds) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['affectedAttemptIds'],
            message: 'A scientific correction must name the results it concerns, ' +
                'because each of them has to be approved again before the ' +
                'replacement can be issued.',
        });
    }
    if (!needsAffected && value.affectedAttemptIds) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['affectedAttemptIds'],
            message: 'An administrative correction changes no result, so it names none.',
        });
    }
});
/**
 * Suspend, reinstate or cancel — one shape, because each is the same kind of
 * act: a person changing the standing of a document and saying why.
 *
 * THE REASON IS MANDATORY FOR ALL THREE, reinstatement included: a suspension
 * ends only with a documented final decision (D120), and "it was reinstated"
 * with no stated conclusion is not one.
 */
export const reportStatusReasonSchema = z
    .object({
    reason: reasonSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Delivery — the act that is NOT issuance
// ---------------------------------------------------------------------------
/**
 * Record that a document was handed over.
 *
 * THIS RECORDS WHAT A PERSON DID. The system sends nothing in Phase 10, and no
 * field here asks it to (D121): every stored row carries `sentBySystem: false`
 * because that is the actual send result. A staff download is never one of
 * these rows.
 *
 * `receiptConfirmed` may only be set where there is actual evidence to point
 * at, so a laboratory cannot end up with a confirmed receipt nobody can
 * produce. `deliveredAt` is what the recorder states, which may be earlier than
 * now; the backend refuses a date in the future, since nobody can record a
 * handover that has not happened.
 */
export const recordReportDeliverySchema = z
    .object({
    purpose: reportDeliveryPurposeSchema,
    method: reportDeliveryMethodSchema,
    deliveredAt: isoTimestampSchema,
    recipientName: boundedText(1, 200),
    /** Optional: a document is often handed to somebody who is not in the
     *  client's contact list, and the name they gave is still the record. */
    clientContactId: uuidSchema.nullable().optional(),
    receiptConfirmed: z.boolean().optional(),
    /** What the confirmation rests on, in the recorder's words: a signed
     *  delivery note, a reply, a courier reference. */
    receiptEvidence: optionalText(2000),
    note: optionalText(2000),
    /** The status change this notice was about. */
    relatedStatusEventId: uuidSchema.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.receiptConfirmed && !value.receiptEvidence) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['receiptEvidence'],
            message: 'Confirming receipt requires the evidence it rests on. Neither ' +
                'issuing nor sending is proof that anybody received anything.',
        });
    }
    if (value.relatedStatusEventId &&
        value.purpose !== REPORT_DELIVERY_PURPOSE.NOTICE) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['relatedStatusEventId'],
            message: 'Only a notice refers to a status change; a delivery hands over ' +
                'the document itself.',
        });
    }
});
/**
 * Release the payment hold on a report's file.
 *
 * The note is mandatory: somebody is lifting a restriction the laboratory put
 * there, and the record must say on what basis. Releasing changes ACCESS and
 * nothing else — not the version status, not the verification status, not the
 * validity of any result (D115).
 */
export const releasePaymentHoldSchema = z
    .object({
    note: reasonSchema,
})
    .strict();
//# sourceMappingURL=reports.js.map